import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { timeout } from 'rxjs/operators';

/** Client-side ceiling per request; the backend aborts its own queries at 120s. */
export const TRENDS_REQUEST_TIMEOUT_MS = 150_000;

@Injectable({
  providedIn: 'root'
})
export class TrendsService {
  private baseUrl = process.env['API_DOMAIN_NAME'];

  constructor(private http: HttpClient) {}

  private getAuthHeaders(): HttpHeaders {
    const accessToken = localStorage.getItem('authToken');
    if (!accessToken) {
      throw new Error('Access token not available.');
    }
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: accessToken,
    });
  }

  getKeyMetrics(payload: any): Promise<any> {
    return this.http.post(`${this.baseUrl}/trends/stats/key-metrics`, payload, {
      headers: this.getAuthHeaders()
    }).toPromise();
  }

  getInsightsMetrics(payload: any): Promise<any> {
    return this.http.post(`${this.baseUrl}/trends/insights-metrics/insights-metrics`, payload, {
      headers: this.getAuthHeaders()
    }).toPromise();
  }

  // ---- Granular endpoints (one request per metric group / insights column, fired in parallel) ----
  // Observables (not promises) so in-flight calls can be cancelled when dates/filters change.

  getTotalConversations$(payload: any): Observable<any> {
    return this.http
      .post(`${this.baseUrl}/trends/stats/total-conversations`, payload, { headers: this.getAuthHeaders() })
      .pipe(timeout(TRENDS_REQUEST_TIMEOUT_MS));
  }

  getAverageDurations$(payload: any): Observable<any> {
    return this.http
      .post(`${this.baseUrl}/trends/stats/average-durations`, payload, { headers: this.getAuthHeaders() })
      .pipe(timeout(TRENDS_REQUEST_TIMEOUT_MS));
  }

  /** payload must carry a single `insights_column`. */
  getInsightsColumn$(payload: any): Observable<any> {
    return this.http
      .post(`${this.baseUrl}/trends/insights-metrics/column`, payload, { headers: this.getAuthHeaders() })
      .pipe(timeout(TRENDS_REQUEST_TIMEOUT_MS));
  }

  clearStatsCache(): Promise<any> {
    return this.http.delete(`${this.baseUrl}/trends/stats/cache/clear`, {
      headers: this.getAuthHeaders()
    }).toPromise();
  }

  getStatsCacheStats(): Promise<any> {
    return this.http.get(`${this.baseUrl}/trends/stats/cache/stats`, {
      headers: this.getAuthHeaders()
    }).toPromise();
  }

  clearInsightsCache(): Promise<any> {
    return this.http.delete(`${this.baseUrl}/trends/insights-metrics/cache/clear`, {
      headers: this.getAuthHeaders()
    }).toPromise();
  }

  getInsightsCacheStats(): Promise<any> {
    return this.http.get(`${this.baseUrl}/trends/insights-metrics/cache/stats`, {
      headers: this.getAuthHeaders()
    }).toPromise();
  }
}
